import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProjectById } from '../../services/projectService';
import GanttChart from '../../components/GanttChart/GanttChart';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import logo from '../../assets/icon.png';
import {
  CalendarDays,
  BadgeEuro,
  Clock3,
  FileDown,
  FileScan,
  Building2,
  ListOrdered,
  TrendingUp,
  CheckCircle2,
  Clock,
  MapPin,
  ExternalLink,
  Wallet,
  Briefcase,
  MessageSquare,
  Info,
} from 'lucide-react';
import {
  getProcedureLink,
  getProcedureTypeLabel,
  getProjectSourceLabel,
} from '../../utils/procedureLinks';
import PremiumAIReport from '../../components/PremiumAIReport/PremiumAIReport';
import ProcedurePaywall from '../../components/ProcedurePaywall/ProcedurePaywall';
import { slugify } from '../../utils/slugify';
//import html2pdf from 'html2pdf.js';
import { saveAs } from 'file-saver';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const safeDateStr = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateShort = (dateStr) => {
  const d = safeDateStr(dateStr);
  if (!d) return dateStr || '–';
  return d.toLocaleDateString('ro-RO', { month: 'short', year: 'numeric' });
};

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getProjectById(id);
        setProject(data);
      } catch (err) {
        console.error('Eroare la încărcarea proiectului:', err);
        setError('Nu s-au putut încărca detaliile proiectului.');
      } finally {
        if (window.prerenderReady !== undefined) {
          window.prerenderReady = true;
        }
      }
    };
    fetchData();
  }, [id]);

  const handleDownloadReport = () => {
    if (project?.reportUrl) {
      saveAs(project.reportUrl, `raport_proiect_${project.id}.pdf`);
    }
  };

  const handleExportPDF = () => {
    if (printRef.current) {
      // html2pdf()
      //   .set({ margin: 0.5, filename: `detalii_proiect_${project.id}.pdf` })
      //   .from(printRef.current)
      //   .save();
    }
  };

  if (error) {
    return (
      <p className="text-center py-10 text-red-600">
        {error}
      </p>
    );
  }

  if (!project) {
    return (
      <p className="text-center py-10 text-gray-600 animate-pulse">
        Se încarcă detaliile proiectului...
      </p>
    );
  }

  const ganttProcedures = [
    ...(project.currentProcedures || []).map((proc, idx) => ({
      ...proc,
      type: 'actual',
      index: idx + 1,
      color: '#6366f1',
      barColor: '#6366f1',
      labelColor: '#312e81',
    })),
    ...(project.futureProcedures || []).map((proc, idx) => ({
      ...proc,
      type: 'future',
      index: (project.currentProcedures?.length || 0) + idx + 1,
      color: '#a5b4fc',
      barColor: '#a5b4fc',
      labelColor: '#3730a3',
      isFuture: true,
    })),
  ];

  const totalProcedures =
    (project.currentProcedures?.length || 0) + (project.futureProcedures?.length || 0);
  const activeProcedures = project.currentProcedures?.length || 0;
  const futureProcedures = project.futureProcedures?.length || 0;

  const allProcedures = [
    ...(project.currentProcedures || []),
    ...(project.futureProcedures || []),
  ];
  let actualStartDate = project.startDate;
  let actualEndDate = project.endDate;

  if (allProcedures.length > 0) {
    const allDates = allProcedures
      .flatMap((proc) => [safeDateStr(proc.startDate), safeDateStr(proc.endDate)])
      .filter(Boolean);
    if (allDates.length > 0) {
      actualStartDate = new Date(Math.min(...allDates)).toLocaleDateString('ro-RO');
      actualEndDate = new Date(Math.max(...allDates)).toLocaleDateString('ro-RO');
    }
  }

  const statusData = [
    { name: 'Active', value: activeProcedures, color: '#6366f1' },
    { name: 'Estimate', value: futureProcedures, color: '#a5b4fc' },
  ];

  const getMonthlyDistribution = () => {
    const months = {};
    [
      ...(project.currentProcedures || []),
      ...(project.futureProcedures || []),
    ].forEach((proc) => {
      const start = safeDateStr(proc.startDate);
      if (!start) return;
      const monthKey = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = start.toLocaleDateString('ro-RO', { month: 'short', year: 'numeric' });
      if (!months[monthKey]) {
        months[monthKey] = { month: monthLabel, key: monthKey, count: 0, value: 0 };
      }
      months[monthKey].count += 1;
      months[monthKey].value += proc.estimatedValue || 0;
    });
    return Object.values(months).sort((a, b) => a.key.localeCompare(b.key));
  };

  const monthlyData = getMonthlyDistribution();

  const sourceLabel = getProjectSourceLabel(project.groupingMetadata?.source);

  // Collect distinct values from procedures for the info section
  const distinctFundingSources = [...new Set(
    (project.currentProcedures || []).map(p => p.fundingSource).filter(Boolean)
  )];
  const distinctContractTypes = [...new Set(
    (project.currentProcedures || []).map(p => p.contractType).filter(Boolean)
  )];
  const distinctProcedureTypes = [...new Set(
    (project.currentProcedures || []).map(p => p.procedureType).filter(Boolean)
  )];

  const hasProjectInfo = sourceLabel || project.fundingSource || project.contractTypeName ||
    project.procedureTypeName || project.paapYear > 0 ||
    distinctFundingSources.length > 0 || distinctContractTypes.length > 0;

  // SEO metadata
  const pageTitle = `${project.projectTitle} - Detalii Proiect - ${import.meta.env.VITE_NAME}`;
  const pageDescription = `Detalii complete despre proiectul ${project.projectTitle} - Autoritate: ${project.authorityName}. Valoare estimată: ${project.estimatedValue?.toLocaleString('ro-RO')} ${project.currency || 'RON'}. Durată: ${project.durationExecution} luni. ${totalProcedures} proceduri planificate.`;
  const pageKeywords = `proiect, ${project.projectTitle}, ${project.authorityName}, proceduri achizitii, ${project.county || ''}, licitatii constructii, SEAP`;
  const projectSlug = slugify(project.projectTitle);
  const pageUrl = `${window.location.origin}/proiecte/${projectSlug}/${id}`;
  const canonicalUrl = pageUrl;

  return (
    <>
      <SeoPageComponent
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        pageUrl={pageUrl}
        logoUrl={logo}
        keywords={pageKeywords}
        canonicalUrl={canonicalUrl}
      />
      <section className="max-w-7xl mx-auto px-2 sm:px-6 py-8 space-y-10">
        {/* Summary Bar Sticky */}
        <div className="sticky top-0 z-20 bg-gradient-to-r from-indigo-50 via-white to-emerald-50 border-b border-indigo-100 shadow-sm py-4 px-2 sm:px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1
              className="text-2xl sm:text-3xl font-extrabold text-indigo-700 flex items-center gap-2 break-words whitespace-normal max-w-full"
              style={{ wordBreak: 'break-word' }}
            >
              <FileScan size={28} className="text-indigo-400" />
              <span className="block max-w-full break-words whitespace-normal">
                {project.projectTitle}
              </span>
            </h1>
            <div className="flex flex-wrap gap-2 mt-2">
              {/* Authority name hidden for free users (only general county stays visible) */}
              {!project.isPartial && project.authorityName && (
                <span className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs font-semibold px-3 py-1 rounded-full">
                  <Building2 size={14} /> {project.authorityName}
                </span>
              )}
              <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">
                <BadgeEuro size={14} /> {project.estimatedValue?.toLocaleString('ro-RO')} {project.currency || 'RON'}
              </span>
              {project.county && (
                <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 text-xs font-semibold px-3 py-1 rounded-full">
                  <MapPin size={14} /> {project.city ? `${project.city}, ` : ''}{project.county}
                </span>
              )}
              <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                <Clock3 size={14} /> {project.durationExecution} luni
              </span>
              <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-xs font-semibold px-3 py-1 rounded-full">
                <CalendarDays size={14} /> {project.startDate} –{' '}
                {project.endDate}
              </span>
              {sourceLabel && (
                <span className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs font-semibold px-3 py-1 rounded-full">
                  {sourceLabel}
                </span>
              )}
              {project.paapYear > 0 && (
                <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 text-xs font-semibold px-3 py-1 rounded-full">
                  PAAP {project.paapYear}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleDownloadReport}
              className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg shadow hover:bg-emerald-700 transition font-semibold text-sm"
            >
              <FileDown size={18} /> Descarcă raport
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg shadow hover:bg-indigo-700 transition font-semibold text-sm"
            >
              <FileScan size={18} /> Export PDF
            </button>
          </div>
        </div>

        {/* Main Content */}
        <section ref={printRef} className="space-y-10">
          {/* Info Card — Autoritate + Valori */}
          <section className="bg-white p-8 rounded-2xl shadow-lg border border-indigo-100">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Building2 size={20} className="text-indigo-600" /> Autoritate
              Contractantă
            </h2>
            <p className="text-base text-gray-700 mb-2 font-medium">
              {project.authorityName}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
              <div className="bg-gradient-to-br from-green-50 to-white p-5 rounded-xl border border-green-100 shadow-sm">
                <p className="text-xs text-gray-500 mb-1 flex items-center gap-2">
                  <BadgeEuro size={18} className="text-green-600" /> Valoare
                  estimată totală
                </p>
                <p className="text-2xl font-bold text-gray-800">
                  {project.estimatedValue?.toLocaleString('ro-RO')} {project.currency || 'RON'}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-white p-5 rounded-xl border border-blue-100 shadow-sm">
                <p className="text-xs text-gray-500 mb-1 flex items-center gap-2">
                  <Clock3 size={18} className="text-blue-600" /> Durată execuție
                </p>
                <p className="text-2xl font-bold text-gray-800">
                  {project.durationExecution} luni
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-5 rounded-xl border border-purple-100 shadow-sm">
                <p className="text-xs text-gray-500 mb-1 flex items-center gap-2">
                  <CalendarDays size={18} className="text-purple-600" />{' '}
                  Perioadă estimată
                </p>
                <p className="text-2xl font-bold text-gray-800">
                  {project.startDate} – {project.endDate}
                </p>
              </div>
            </div>
          </section>

          {/* Proceduri + Raport AI — inline */}
          <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Proceduri — 8/12 */}
            <div className="lg:col-span-8 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <ListOrdered className="w-5 h-5 text-indigo-600" /> Proceduri
                  <span className="text-sm font-normal text-gray-500 ml-1">
                    ({activeProcedures} active{futureProcedures > 0 ? `, ${futureProcedures} planificate` : ''})
                  </span>
                </h2>
                {/* Info badges inline */}
                <div className="flex flex-wrap gap-1.5">
                  {project.contractTypeName && <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded font-medium">{project.contractTypeName}</span>}
                  {project.procedureTypeName && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded font-medium">{project.procedureTypeName}</span>}
                  {project.fundingSource && <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded font-medium">{project.fundingSource}</span>}
                </div>
              </div>
              <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                {(project.currentProcedures || []).map((proc, i) => {
                  // Project.CurrentProcedures embeds AuctionId (e.g. "CN1089020") but not the
                  // procedure's Mongo ObjectId, so fall back to auctionId — the notice-detail
                  // endpoint accepts either via GetNoticeDetailByIdOrNoticeNo.
                  const procLink = getProcedureLink(proc.mongoId || proc.auctionId, proc.collectionSource, proc.procedureTitle || proc.auctionTitle);
                  const typeLabel = getProcedureTypeLabel(proc.collectionSource);
                  const rowContent = (
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {proc.auctionId && (
                            <span className={`inline-flex items-center gap-1 text-sm font-bold px-2 py-0.5 rounded-lg border ${procLink ? 'text-blue-700 bg-blue-50 border-blue-300' : 'text-gray-700 bg-gray-100 border-gray-300'}`}>
                              {proc.auctionId} {procLink && <ExternalLink size={12} />}
                            </span>
                          )}
                          {typeLabel && <span className="text-xs text-white bg-indigo-500 px-2 py-0.5 rounded-full font-semibold">{typeLabel}</span>}
                          {proc.projectPhase && <span className="text-xs text-white bg-indigo-600 px-2 py-0.5 rounded-full font-semibold">{proc.projectPhase}</span>}
                          {proc.noticeStatus && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${proc.noticeStatus === 'Atribuit' ? 'bg-green-100 text-green-700' : proc.noticeStatus === 'Publicat' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{proc.noticeStatus}</span>
                          )}
                        </div>
                        <h4 className={`text-sm font-semibold mb-1 leading-snug ${procLink ? 'text-gray-900 group-hover:text-indigo-700' : 'text-gray-900'}`}>{proc.procedureTitle}</h4>
                        <div className="flex items-center gap-3 text-xs flex-wrap">
                          {proc.estimatedValue > 0 && <span className="font-bold text-green-700">{proc.estimatedValue?.toLocaleString('ro-RO')} {proc.currency || 'RON'}</span>}
                          <span className="text-gray-500"><CalendarDays size={11} className="inline mr-0.5" />{formatDateShort(proc.startDate)} - {formatDateShort(proc.endDate)}</span>
                          {proc.cpvCode && <span className="text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded" title={proc.cpvName}>CPV: {proc.cpvCode}</span>}
                          {proc.contractType && <span className="text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded">{proc.contractType}</span>}
                          {proc.procedureType && <span className="text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">{proc.procedureType}</span>}
                          {proc.fundingSource && <span className="text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded">{proc.fundingSource}</span>}
                        </div>
                        {proc.winnerName && (
                          <div className="mt-1 text-xs text-green-800 bg-green-50 rounded px-2 py-1 inline-flex items-center gap-1">
                            <CheckCircle2 size={11} /> {proc.winnerName} {proc.contractValue > 0 && `- ${proc.contractValue?.toLocaleString('ro-RO')} RON`}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                  return procLink ? (
                    <Link key={i} to={procLink} className="group block px-4 py-3 hover:bg-indigo-50/40 transition-colors">
                      {rowContent}
                    </Link>
                  ) : (
                    <div key={i} className="px-4 py-3">{rowContent}</div>
                  );
                })}
                {(project.futureProcedures || []).map((proc, i) => (
                  <div key={`f-${i}`} className="px-4 py-3 bg-amber-50/40 border-l-4 border-dashed border-amber-300">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white border-2 border-dashed border-amber-400 text-amber-600 flex items-center justify-center font-bold text-xs">P</div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-amber-800 mb-1">{proc.procedureTitle}</h4>
                        <div className="flex items-center gap-3 text-xs flex-wrap">
                          {proc.estimatedValue > 0 && <span className="font-bold text-green-700">{proc.estimatedValue?.toLocaleString('ro-RO')} {proc.currency || 'RON'}</span>}
                          <span className="text-amber-600"><CalendarDays size={11} className="inline mr-0.5" />{formatDateShort(proc.startDate)} - {formatDateShort(proc.endDate)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Raport AI Premium — 4/12 */}
            <div className="lg:col-span-4">
              <PremiumAIReport noticeNo={id} type="project" buttonLabel="Genereaza Raport AI Proiect" modalTitle="Raport AI Proiect" projectTitle={project.projectTitle} />
            </div>
          </section>

          {/* Observatii */}
          {project.observations && (
            <section className="bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4 flex items-start gap-3">
              <MessageSquare size={20} className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                <span className="font-bold">Observatii:</span> {project.observations}
              </p>
            </section>
          )}

          {/* Pipeline Faze Proiect */}
          {project.summary?.phases?.length > 0 && (
            <section className="bg-white p-6 rounded-2xl shadow-lg border border-indigo-100">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp size={20} className="text-indigo-600" /> Pipeline Proiect
                {project.summary?.projectStatus && (
                  <span className="ml-2 text-sm font-semibold bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full">
                    {project.summary.projectStatus}
                  </span>
                )}
              </h2>
              <div className="flex flex-wrap gap-3">
                {project.summary.phases.map((phase, i) => {
                  const phaseIcons = { Intentie: '📋', Planificare: '📅', Proiectare: '📐', Executie: '🏗️', Dirigentie: '👷', Supervizare: '🔍', Dotare: '🛋️', Furnizare: '📦', Atribuire: '✅', Altele: '📄' };
                  const statusColors = {
                    'Atribuit': 'bg-green-50 border-green-300 text-green-700',
                    'In licitatie': 'bg-blue-50 border-blue-300 text-blue-700',
                    'Planificat': 'bg-amber-50 border-amber-300 text-amber-700',
                  };
                  return (
                    <div key={i} className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 ${statusColors[phase.status] || 'bg-gray-50 border-gray-300 text-gray-700'}`}>
                      <span className="text-xl">{phaseIcons[phase.phase] || '📄'}</span>
                      <div>
                        <p className="font-bold text-sm">{phase.phase}</p>
                        <p className="text-xs opacity-80">{phase.status} - {phase.procedureCount} proc.</p>
                        {phase.totalValue > 0 && (
                          <p className="text-xs font-semibold">{phase.totalValue?.toLocaleString('ro-RO')} {phase.currency || 'RON'}</p>
                        )}
                      </div>
                      {i < project.summary.phases.length - 1 && (
                        <span className="text-gray-300 text-xl ml-2">→</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {project.summary?.cpvCodes?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  <span className="text-xs text-gray-500 font-semibold mr-1">Coduri CPV:</span>
                  {project.summary.cpvCodes.map((cpv, i) => (
                    <span key={i} className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full border border-indigo-200">{cpv}</span>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Statistics Dashboard — 5 KPIs */}
          <section className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl shadow-lg p-5 text-white">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-white/20 rounded-lg"><ListOrdered className="w-6 h-6" /></div>
                <TrendingUp className="w-5 h-5 text-indigo-200" />
              </div>
              <p className="text-xs text-indigo-100 mb-1">Total Proceduri</p>
              <p className="text-3xl font-bold">{totalProcedures}</p>
              <p className="text-xs text-indigo-200 mt-1">{activeProcedures} active{futureProcedures > 0 ? ` + ${futureProcedures} plan.` : ''}</p>
            </div>

            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl shadow-lg p-5 text-white">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-white/20 rounded-lg"><CalendarDays className="w-6 h-6" /></div>
                <Clock className="w-5 h-5 text-emerald-200" />
              </div>
              <p className="text-xs text-emerald-100 mb-1">Inceput Proiect</p>
              <p className="text-xl font-bold">{actualStartDate}</p>
              <p className="text-xs text-emerald-200 mt-1">Prima procedura</p>
            </div>

            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl shadow-lg p-5 text-white">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-white/20 rounded-lg"><CalendarDays className="w-6 h-6" /></div>
                <CheckCircle2 className="w-5 h-5 text-purple-200" />
              </div>
              <p className="text-xs text-purple-100 mb-1">Finalizare Proiect</p>
              <p className="text-xl font-bold">{actualEndDate}</p>
              <p className="text-xs text-purple-200 mt-1">Ultima procedura</p>
            </div>

            {project.county && (
              <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl shadow-lg p-5 text-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-white/20 rounded-lg"><MapPin className="w-6 h-6" /></div>
                </div>
                <p className="text-xs text-orange-100 mb-1">Locatie</p>
                <p className="text-xl font-bold">{project.county}</p>
                {project.city && <p className="text-xs text-orange-200 mt-1">{project.city}</p>}
              </div>
            )}

            {sourceLabel && (
              <div className="bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-xl shadow-lg p-5 text-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-white/20 rounded-lg"><Info className="w-6 h-6" /></div>
                </div>
                <p className="text-xs text-cyan-100 mb-1">Sursa date</p>
                <p className="text-xl font-bold">{sourceLabel}</p>
                {project.contractTypeName && <p className="text-xs text-cyan-200 mt-1">{project.contractTypeName}</p>}
              </div>
            )}
          </section>

          {/* Charts Section */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Donut Chart - Procedure Status */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <ListOrdered className="w-5 h-5 text-indigo-600" />
                Status Proceduri
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    labelStyle={{ fontSize: '13px', fontWeight: 'bold' }}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="text-center p-2 bg-indigo-50 rounded-lg">
                  <p className="text-xl font-bold text-indigo-700">
                    {activeProcedures}
                  </p>
                  <p className="text-xs text-gray-600">Active</p>
                </div>
                <div className="text-center p-2 bg-purple-50 rounded-lg">
                  <p className="text-xl font-bold text-purple-700">
                    {futureProcedures}
                  </p>
                  <p className="text-xs text-gray-600">Estimate</p>
                </div>
              </div>
            </div>

            {/* Line Chart - Monthly Distribution */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
                Distribuție Lunară Proceduri
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11 }}
                    stroke="#6b7280"
                  />
                  <YAxis tick={{ fontSize: 11 }} stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    name="Proceduri"
                    stroke="#6366f1"
                    strokeWidth={2}
                    dot={{ fill: '#6366f1', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Sectiunea veche de proceduri a fost mutata inline cu Raport AI mai sus */}

          {/* Gantt Chart - Full Width (premium only) */}
          {!project.isPartial && (
            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <ListOrdered size={22} className="text-indigo-600" /> Gantt:
                Timeline Proceduri
              </h2>
              <div className="w-full overflow-x-auto rounded-2xl border border-indigo-100 bg-white shadow-lg p-6">
                <GanttChart procedures={ganttProcedures} />
              </div>
            </section>
          )}
        </section>

        {/* Paywall for free / unauthenticated users — shown AFTER the partial content */}
        {project.isPartial && <ProcedurePaywall />}
      </section>
    </>
  );
};

export default ProjectDetail;
